import { Link, Link as RouterLink } from "react-router-dom";
import { ArrowRight, Compass, Zap, ShieldCheck, Users, Factory, Cpu, Wrench, Layers } from "lucide-react";
import WorkflowDiagram from "@/components/WorkflowDiagram";

const audience = [
  { label: "Independent Inventors", icon: Compass },
  { label: "Hardware Startups", icon: Zap },
  { label: "Product-Development Teams", icon: Users },
  { label: "Small & Midsized Manufacturers", icon: Factory },
  { label: "Engineering & Fabrication Partners", icon: Wrench },
];

export default function Landing() {
  return (
    <div className="space-y-24">
      {/* Hero */}
      <section className="relative">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 items-start">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="tag tag-blue">Systems Architect Preview</span>
              <span className="tag tag-gold">Talons Inclusion Factory</span>
            </div>
            <div className="font-tech mb-4">AI-Integrated Universal Design Engine</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05]">
              A.I.U.D.E.
              <span className="block text-slate-200 text-2xl sm:text-3xl lg:text-4xl mt-4 font-normal">
                Describe What You Need.<br />
                Receive a Structured Path Toward Building It.
              </span>
            </h1>
            <p className="text-slate-300 text-base leading-relaxed mt-8 max-w-2xl">
              A.I.U.D.E. is being developed to connect design intent, engineering specifications,
              validation results, and manufacturing preparation through one AI-guided workflow.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link to="/start" className="btn-primary" data-testid="cta-start-project">
                Start a Design Project <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/validated-demo" className="btn-ghost" data-testid="cta-view-demo">
                View Validated Demonstration
              </Link>
            </div>
            <div className="dim-line mt-16" />
            <div className="grid grid-cols-3 gap-6 mt-6">
              {[
                ["Solver", "CalculiX"],
                ["Exports", "STEP / STL"],
                ["Discipline", "Mechanical"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="metric-label">{k}</div>
                  <div className="font-mono text-lg text-white mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right rig: blueprint viewport */}
          <div className="panel-elev tick-brackets p-6" data-testid="landing-demo-excerpt">
            <div className="flex items-center justify-between mb-2 gap-3">
              <span className="font-tech text-[#C9A961] leading-tight">Excerpt from the Validated Mechanical Bracket Demonstration</span>
              <span className="tag tag-green"><span className="led-pass mr-2 align-middle" />Verified</span>
            </div>
            <Link to="/validated-demo" data-testid="landing-demo-fulllink" className="font-mono text-[11px] text-[#00D2FF] hover:underline block mb-4">
              View full validated demonstration →
            </Link>
            <div className="cad-grid aspect-[4/3] relative border border-[#1E2F48]">
              <svg viewBox="0 0 400 300" className="w-full h-full">
                <g stroke="#00D2FF" strokeWidth="1.2" fill="none" opacity="0.85">
                  <rect x="60" y="70" width="280" height="160" />
                  <line x1="60" y1="150" x2="340" y2="150" strokeDasharray="4 4" opacity="0.5" />
                  <line x1="200" y1="70" x2="200" y2="230" strokeDasharray="4 4" opacity="0.5" />
                  <circle cx="100" cy="110" r="6" />
                  <circle cx="300" cy="110" r="6" />
                  <circle cx="100" cy="190" r="6" />
                  <circle cx="300" cy="190" r="6" />
                  <path d="M60,70 L40,50 M340,70 L360,50 M60,230 L40,250 M340,230 L360,250" />
                </g>
                <g stroke="#C9A961" strokeWidth="0.8" fill="none">
                  <line x1="60" y1="40" x2="340" y2="40" strokeDasharray="6 4" />
                  <text x="200" y="34" fill="#C9A961" fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle">Verified CalculiX Run</text>
                  <line x1="30" y1="70" x2="30" y2="230" strokeDasharray="6 4" />
                  <text x="18" y="150" fill="#C9A961" fontSize="10" fontFamily="JetBrains Mono" transform="rotate(-90 18 150)" textAnchor="middle">Bracket Demo</text>
                </g>
                <g fill="#64748B" fontFamily="JetBrains Mono" fontSize="9">
                  <text x="66" y="266">A.I.U.D.E. • Validated Bracket</text>
                  <text x="270" y="266">Solver: CalculiX</text>
                </g>
              </svg>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-6">
              {[
                ["Displacement", "0.4147 mm"],
                ["Max stress", "34.31 MPa"],
                ["Yield ratio", "8.04×"],
              ].map(([k, v]) => (
                <div key={k} className="panel p-3">
                  <div className="metric-label">{k}</div>
                  <div className="font-mono text-[#00D2FF] text-lg mt-1">{v}</div>
                </div>
              ))}
            </div>
            <p className="text-[10px] font-mono text-slate-500 mt-4 leading-relaxed">
              Fixed values from the verified CalculiX bracket run — not a live visitor result.
            </p>
          </div>
        </div>
      </section>

      {/* Problem statement */}
      <section className="panel-elev p-8 lg:p-12">
        <div className="flex items-center gap-3 mb-4">
          <Cpu className="w-4 h-4 text-[#C9A961]" />
          <span className="font-tech">The Problem</span>
        </div>
        <p className="text-slate-200 text-lg lg:text-xl leading-relaxed max-w-4xl">
          Physical-product development remains divided among disconnected CAD applications,
          simulation programs, engineering specialists, material databases, suppliers, and
          manufacturing systems. A.I.U.D.E. is designed to make that process more coherent,
          traceable, and accessible.
        </p>
      </section>

      {/* Seven-stage workflow */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-tech mb-2">Seven-Stage Workflow</div>
            <h2 className="text-2xl lg:text-3xl font-semibold text-white">Intent → Manufacturing Outputs</h2>
          </div>
          <Link to="/start" className="btn-ghost text-sm" data-testid="workflow-cta">
            Enter the pipeline <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <WorkflowDiagram activeId="intent" />
      </section>

      {/* Who it serves */}
      <section>
        <div className="font-tech mb-2">Who It Serves</div>
        <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-8">Built for people who make things</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {audience.map(({ label, icon: Icon }) => (
            <div key={label} className="panel p-5 hover:border-[#0066FF] transition" data-testid={`audience-${label.split(' ')[0].toLowerCase()}`}>
              <Icon className="w-5 h-5 text-[#C9A961] mb-3" />
              <div className="text-sm text-white leading-snug">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing rail */}
      <section className="panel-elev p-8 lg:p-12">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="font-tech mb-3">Next Step</div>
            <h3 className="text-xl lg:text-2xl text-white font-medium">Ready to translate an idea into engineering artifacts?</h3>
            <p className="text-slate-400 mt-2 text-sm">Begin with a plain-language description. A.I.U.D.E. will convert it into a structured, editable specification.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/start" className="btn-primary" data-testid="footer-cta-intake">Start a Design Project</Link>
            <Link to="/pilot" className="btn-copper" data-testid="footer-cta-pilot">Request a Pilot</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
